import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";

import Papa from "papaparse";
import countryDataMap from "../JavaScript/countryDataMap";

import {
  Chart as chartjs, // The main Chart.js module
  CategoryScale, // For working with category (discrete) axes
  LinearScale, // For working with linear (continuous) axes
  BarElement, // For rendering bar charts
  Title, // For adding titles to charts
  Tooltip, // For displaying tooltips when hovering over chart elements
  Legend, // For showing legends on charts
} from "chart.js";

// Registering the imported Chart.js components to make them usable in the chart configuration
chartjs.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

function ComparisonChart({ country1, country2, year, setValue1, setValue2 }) {
  // Default x-axis size for chart
  const defaultSize = 32;

  // To store raw parsed CSV data (IMPORTANT: stored once)
  const [country1Data, setCountry1Data] = useState([]);
  const [country2Data, setCountry2Data] = useState([]);

  // To store the DataSet of the First Country
  const [dataSet1, setDataSet1] = useState({
    data: Array(defaultSize).fill(0),
  });

  // To store the DataSet of the Second Country
  const [dataSet2, setDataSet2] = useState({
    data: Array(defaultSize).fill(0),
  });

  // To store the Labels on the x-axis of the Chart (year)
  const [labels] = useState(
    [...Array(defaultSize).keys()].map((i) => 1990 + i),
  );

  /**
   * Parse the CSV file of a country
   * This runs ONLY when country changes
   */
  const loadCountryData = (countryName, setRawData) => {
    const csvFile = countryDataMap[countryName];

    Papa.parse(csvFile, {
      download: true, // Downloads the CSV file
      header: true, // Treats the first row as the header
      dynamicTyping: true, // Converts numeric strings to actual numbers
      complete: (result) => {
        // This function is called when parsing is done
        if (result.errors.length > 0) {
          console.error("Parsing errors:", result.errors);
          return;
        }

        // Extract and clean up the year and percentage data
        const cleanedData = result.data
          .map((item) => ({
            Year: parseInt(item[' "Year"']?.toString().trim(), 10),
            Percent: parseFloat(item[' "Percent"']?.toString().trim()),
          }))
          // Filter out invalid data
          .filter((item) => !isNaN(item.Year) && !isNaN(item.Percent));

        setRawData(cleanedData);
      },
    });
  };

  /**
   * Load country 1 data when country1 changes
   */
  useEffect(() => {
    if (country1) {
      loadCountryData(country1, setCountry1Data);
    }
  }, [country1]);

  /**
   * Load country 2 data when country2 changes
   */
  useEffect(() => {
    if (country2) {
      loadCountryData(country2, setCountry2Data);
    }
  }, [country2]);

  /**
   * Update chart dataset for country1 when:
   * - year changes
   * - country1Data changes
   */
  useEffect(() => {
    if (!country1Data.length) return;

    const backgroundColors = country1Data.map((item) =>
      item.Year === Number(year) ? "#be0000" : "#ffaaaa",
    );

    const selected = country1Data.find((item) => item.Year === Number(year));

    if (selected) {
      setValue1(selected.Percent);
    }

    setDataSet1({
      label: country1,
      data: country1Data.map((item) => item.Percent),
      borderColor: "black",
      backgroundColor: backgroundColors,
    });
  }, [year, country1Data]);

  /**
   * Update chart dataset for country2 when:
   * - year changes
   * - country2Data changes
   */
  useEffect(() => {
    if (!country2Data.length) return;

    const backgroundColors = country2Data.map((item) =>
      item.Year === Number(year) ? "#0000be" : "#aaaaff",
    );

    const selected = country2Data.find((item) => item.Year === Number(year));

    if (selected) {
      setValue2(selected.Percent);
    }

    setDataSet2({
      label: country2,
      data: country2Data.map((item) => item.Percent),
      borderColor: "black",
      backgroundColor: backgroundColors,
    });
  }, [year, country2Data]);

  // Variable used to render the chart with State variables
  const data = {
    labels: labels,
    datasets: [dataSet1, dataSet2],
  };

  // Options for rendering the chart
  const chartOptions = {
    animation: false,
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "Prevalence of Cardiovascular Diseases (%) Per Country",
      },
    },
  };

  return (
    <div className="comparison-chart">
      {/* Render the bar chart */}
      <Bar options={chartOptions} data={data} />
    </div>
  );
}

export default ComparisonChart;
